import ContentWrapper from '@components/ContentWrapper'
import ErrorFallback from '@components/ErrorFallback'
import MaintenanceModal from '@components/MaintenanceModal'
import ModalDialog from '@components/ModalDialog'
import SuspenseFallback from '@components/SuspenseFallback'
import { ReportProblem } from '@views/ReportProblem'
import TestMethods from '@views/TestMethods'
import { configuration, useLogErrorRequest } from 'mmfintech-checkout-commons'
import { GlobalContextProvider } from 'mmfintech-commons'
import { Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { withTranslation } from 'react-i18next'
import { Redirect, Route, type RouteProps, BrowserRouter as Router, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'

import routes from './routes'
import settings from './settings'
import theme from './theme'

const AppInner = () => {
  const [modalVisible, setModalVisible] = useState(false)

  const globalContext = {
    modalShow: () => setModalVisible(true)
  }

  useEffect(() => {
    configuration.createBackendConfig(settings.backendForLocalhost)
    if (settings.cardGatewayForLocalhost) {
      configuration.createCardGatewayConfig(settings.cardGatewayForLocalhost)
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <GlobalContextProvider context={globalContext}>
        <Router>
          <ContentWrapper>
            <MaintenanceModal />

            <Switch>
              {!configuration.isProduction() ? (
                <CustomRoute path='/methods' exact={true} component={TestMethods} />
              ) : null}
              {routes.map(route => {
                const { path, exact, redirect, component } = route

                if (redirect) {
                  return (
                    <Route key={path} exact={exact} path={path}>
                      <Redirect to={redirect} />
                    </Route>
                  )
                }

                if (component) {
                  return <CustomRoute key={path} path={path} exact={exact} component={component} />
                }

                return null
              })}
            </Switch>

            <ModalDialog
              content={<ReportProblem visible={modalVisible} onClose={() => setModalVisible(false)} />}
              visible={modalVisible}
              onClose={() => setModalVisible(false)}
            />
          </ContentWrapper>

          <Toaster
            position='top-right'
            containerStyle={{
              top: '10rem'
            }}
            toastOptions={{
              className: '',
              style: {
                color: 'black',
                fontFamily: 'inherit',
                fontSize: '1.4rem',
                fontStyle: 'normal',
                padding: '1.5rem',
                borderRadius: '0'
              },
              success: {}
            }}
          />
        </Router>
      </GlobalContextProvider>
    </ThemeProvider>
  )
}

const ThisApp = withTranslation()(AppInner)

const App = () => {
  const { logErrorRequest } = useLogErrorRequest()

  const handleError = (error: Error, info: { componentStack: string }) => {
    logErrorRequest(error.toString(), info?.componentStack)
  }

  const handleReset = () => {
    window.location.replace('/')
  }

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError} onReset={handleReset}>
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App

const CustomRoute = ({ component: Component, ...rest }: RouteProps) => (
  <Route {...rest} render={props => <Component {...props} />} />
)
